import * as Cesium from 'cesium';
import { prefersReducedMotion } from '../util/motion';
import {
  SKY_VIEW_AIM_DURATION_S,
  SKY_VIEW_EYE_HEIGHT_M,
  SKY_VIEW_FLY_DURATION_S,
  SKY_VIEW_FOV_DEG,
  SKY_VIEW_HEADING_DEG,
  SKY_VIEW_PITCH_DEG,
  SKY_VIEW_ROLL_DEG,
  elevationFromEnu,
  headingFromEnu,
  resolveAltitudeMeters,
  toRadians,
} from './skyCameraMath';

/**
 * Phase 7 chunk 1B — Cesium-using sky-view camera helpers.
 *
 * Splits cleanly from `skyCameraMath.ts` (pure constants + angle math,
 * Vitest-friendly) so this file's Cesium-touching logic lives in one
 * place and is exercised by Playwright e2e in chunk 1C instead of
 * unit tests.
 *
 * Cesium camera orientation semantics:
 *  - heading: rotation around the local up axis. 0 = north.
 *  - pitch:   rotation around the right axis. 0 = looking at the
 *    horizon, +π/2 = straight up at zenith. We use +75° so the user
 *    looks 15° below true zenith — a more natural stargazer posture.
 *  - roll:    rotation around the forward axis. Always 0 for sky view.
 *
 * Camera.flyTo interprets heading/pitch/roll relative to the local
 * east-north-up frame at the destination cartesian, so heading 0
 * "just works" as north-up without any explicit Matrix4 framing.
 */

/** Snapshot of the camera state that needs restoring on sky-view exit. */
export interface CameraSnapshot {
  position: Cesium.Cartesian3;
  heading: number;
  pitch: number;
  roll: number;
  fov: number;
}

/** Snapshot the current camera + frustum so {@link setGlobeView} can
 *  restore it later. Clones the position cartesian so mutations to
 *  `camera.position` after snapshotting don't leak into the snapshot.
 *  Falls back to Cesium's default 60° FOV if the frustum somehow has
 *  an unset `fov` (TypeScript marks it optional even though Cesium
 *  initializes it in PerspectiveFrustum's constructor). */
export function snapshotCamera(viewer: Cesium.Viewer): CameraSnapshot {
  const camera = viewer.camera;
  const frustum = camera.frustum as Cesium.PerspectiveFrustum;
  return {
    position: camera.position.clone(),
    heading: camera.heading,
    pitch: camera.pitch,
    roll: camera.roll,
    fov: frustum.fov ?? toRadians(60),
  };
}

/** Observer subset used by {@link setSkyView}. Matches the public
 *  shape of `Observer` from `resources/js/observer/Observer.ts` so the
 *  caller can pass an Observer directly. */
export interface SkyObserver {
  latitude: number;
  longitude: number;
  altitudeMeters?: number;
}

/** Position the camera at the observer's lat/lon/alt, aimed at the
 *  zenith with north pointing up on screen. Widens FOV to 75° for a
 *  more open sky-feeling field. */
export function setSkyView(
  viewer: Cesium.Viewer,
  observer: SkyObserver,
  durationSeconds = SKY_VIEW_FLY_DURATION_S,
): void {
  // Chunk 2D — lift the camera by SKY_VIEW_EYE_HEIGHT_M (1.7 m) above
  // the observer's altitude so the WGS84 ellipsoid properly occludes
  // when the user drags the view down past the local horizon.
  const altMeters = resolveAltitudeMeters(observer.altitudeMeters) + SKY_VIEW_EYE_HEIGHT_M;
  const destination = Cesium.Cartesian3.fromDegrees(
    observer.longitude,
    observer.latitude,
    altMeters,
  );
  // Phase 8 chunk 1 — instant cut when reduced-motion is requested.
  const effectiveDuration = prefersReducedMotion() ? 0 : durationSeconds;
  viewer.camera.flyTo({
    destination,
    orientation: {
      heading: toRadians(SKY_VIEW_HEADING_DEG),
      pitch: toRadians(SKY_VIEW_PITCH_DEG),
      roll: toRadians(SKY_VIEW_ROLL_DEG),
    },
    duration: effectiveDuration,
  });
  const frustum = viewer.camera.frustum as Cesium.PerspectiveFrustum;
  frustum.fov = toRadians(SKY_VIEW_FOV_DEG);
}

/**
 * Chunk 3E — rotate the sky-view camera to aim at a specific
 * world-space cartesian (typically a satellite's current ECEF
 * position). Camera *position* stays at the observer + eye-height;
 * only heading + pitch change, so the user stays in sky-view framing.
 *
 * Uses Cesium's `flyTo` with `destination = current observer
 * position` so the camera animates the orientation change smoothly
 * over `durationSeconds`. Position interpolation is a no-op (start
 * and end are identical).
 *
 * Caller is expected to be in sky view already; this helper does
 * not validate state. Globe.aimSkyViewAt handles those checks.
 */
export function aimSkyCameraAt(
  viewer: Cesium.Viewer,
  observer: SkyObserver,
  target: Cesium.Cartesian3,
  durationSeconds = SKY_VIEW_AIM_DURATION_S,
): void {
  const altMeters = resolveAltitudeMeters(observer.altitudeMeters) + SKY_VIEW_EYE_HEIGHT_M;
  const observerCart = Cesium.Cartesian3.fromDegrees(
    observer.longitude,
    observer.latitude,
    altMeters,
  );
  // Transform (target - observer) into the local east-north-up frame
  // at the observer so we can read off heading + elevation cleanly.
  const enuFrame = Cesium.Transforms.eastNorthUpToFixedFrame(observerCart);
  const enuFromWorld = Cesium.Matrix4.inverseTransformation(enuFrame, new Cesium.Matrix4());
  const rel = Cesium.Cartesian3.subtract(target, observerCart, new Cesium.Cartesian3());
  const enu = Cesium.Matrix4.multiplyByPointAsVector(enuFromWorld, rel, new Cesium.Cartesian3());

  // Cesium's local-ENU frame: x = east, y = north, z = up.
  const heading = headingFromEnu(enu.x, enu.y);
  const pitch = elevationFromEnu(enu.x, enu.y, enu.z);

  // Phase 8 chunk 1 — instant cut when reduced-motion is requested.
  viewer.camera.flyTo({
    destination: observerCart,
    orientation: {
      heading,
      pitch,
      roll: toRadians(SKY_VIEW_ROLL_DEG),
    },
    duration: prefersReducedMotion() ? 0 : durationSeconds,
  });
}

/**
 * Phase 11 follow-up — rotate the sky-view camera to face a given
 * compass heading while preserving the user's current pitch. Position
 * stays at the observer + eye-height; only heading animates over
 * `durationSeconds`. Roll stays 0 (sky view never rolls).
 *
 * Used by the chunk-2C compass HUD when the user clicks a direction
 * cell — clicking "E" rotates the camera to face east without
 * snapping their vertical look back to the entry-default pitch.
 */
export function aimSkyHeadingDeg(
  viewer: Cesium.Viewer,
  observer: SkyObserver,
  headingDeg: number,
  durationSeconds = SKY_VIEW_AIM_DURATION_S,
): void {
  const altMeters = resolveAltitudeMeters(observer.altitudeMeters) + SKY_VIEW_EYE_HEIGHT_M;
  const observerCart = Cesium.Cartesian3.fromDegrees(
    observer.longitude,
    observer.latitude,
    altMeters,
  );
  // Preserve the user's current pitch — if they've dragged the view
  // down to the horizon, clicking a compass cell should keep that
  // vertical look, not reset to the +75° entry pose.
  const currentPitch = viewer.camera.pitch;
  viewer.camera.flyTo({
    destination: observerCart,
    orientation: {
      heading: toRadians(headingDeg),
      pitch: currentPitch,
      roll: toRadians(SKY_VIEW_ROLL_DEG),
    },
    duration: prefersReducedMotion() ? 0 : durationSeconds,
  });
}

/** Restore the camera + frustum to a snapshotted globe-view state. */
export function setGlobeView(
  viewer: Cesium.Viewer,
  snapshot: CameraSnapshot,
  durationSeconds = SKY_VIEW_FLY_DURATION_S,
): void {
  // Phase 8 chunk 1 — instant cut when reduced-motion is requested.
  viewer.camera.flyTo({
    destination: snapshot.position,
    orientation: {
      heading: snapshot.heading,
      pitch: snapshot.pitch,
      roll: snapshot.roll,
    },
    duration: prefersReducedMotion() ? 0 : durationSeconds,
  });
  const frustum = viewer.camera.frustum as Cesium.PerspectiveFrustum;
  frustum.fov = snapshot.fov;
}
